import { useMemo } from 'react'
import { CalendarRange } from 'lucide-react'
import { Bar, BarChart, CartesianGrid, Legend, XAxis, YAxis } from 'recharts'
import { Bone, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui'
import { cn } from '@/lib/cn'
import type { OrganizationDashboard, OrganizationDashboardWeekDay } from '@/types/dashboard.types'
import { DashboardDetailModal, DashboardTile } from './DashboardTile'
import { ATTENDANCE_SERIES } from './chartSeries'
import { attendanceTone, formatNumber, formatPct, formatWeekRange } from './dashboardFormat'

/** Older servers send only present/recorded; the gap between them is the absence. */
function absentOf(day: OrganizationDashboardWeekDay): number {
  return day.absent ?? Math.max(0, day.recorded - day.present)
}

const CARD_CONFIG = { present: ATTENDANCE_SERIES.present, absent: ATTENDANCE_SERIES.absent }

const DETAIL_CONFIG = {
  onTime: { label: 'On time', theme: ATTENDANCE_SERIES.present.theme },
  late: ATTENDANCE_SERIES.late,
  absent: ATTENDANCE_SERIES.absent,
  onLeave: ATTENDANCE_SERIES.onLeave,
}

function LegendDot({ className, label }: { className: string; label: string }): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
      <span className={cn('h-2 w-2 rounded-full', className)} aria-hidden />
      {label}
    </span>
  )
}

function WeekNote({ data }: { data: OrganizationDashboard }): React.ReactElement {
  const s = data.summary
  return (
    <p className="text-xs text-gray-500 dark:text-gray-400">
      <span className="font-medium text-gray-700 tabular-nums dark:text-gray-200">
        {formatNumber(s.shiftsScheduledWeek)}
      </span>{' '}
      shifts ·{' '}
      <span className="font-medium text-gray-700 tabular-nums dark:text-gray-200">
        {formatNumber(s.totalWorkedHoursWeek)}h
      </span>{' '}
      worked
      {s.avgLateMinutesWeek > 0 ? (
        <>
          {' '}
          · late arrivals average{' '}
          <span className="font-medium text-gray-700 tabular-nums dark:text-gray-200">
            {s.avgLateMinutesWeek}m
          </span>{' '}
          past grace
        </>
      ) : null}
    </p>
  )
}

/**
 * Attended vs absent, day by day this week. Two bars per day so a bad day
 * reads as a short teal bar beside a tall coral one, not as a percentage.
 */
export function AttendanceOverviewCard({
  data,
  isLoading,
  onExpand,
  className,
}: {
  data: OrganizationDashboard | undefined
  isLoading: boolean
  onExpand: () => void
  className?: string
}): React.ReactElement {
  // Stable while the numbers are: a new array would replay the bar animation
  // on every re-render (opening a modal, a 60s refetch).
  const week = data?.weekAttendance
  const days = useMemo(
    () =>
      (week ?? []).map((day) => ({
        label: day.label,
        present: day.present,
        absent: absentOf(day),
      })),
    [week]
  )
  const hasData = days.some((day) => day.present + day.absent > 0)

  return (
    <DashboardTile
      title="Attendance overview"
      subtitle={data ? formatWeekRange(data.weekAttendance.map((d) => d.date)) : 'This week'}
      icon={CalendarRange}
      onExpand={onExpand}
      className={className}
      actions={
        <div className="hidden items-center gap-3 sm:flex">
          <LegendDot className="bg-primary dark:bg-[#4aa3c4]" label="Attended" />
          <LegendDot className="bg-accent" label="Absent" />
        </div>
      }
      footer={data ? <WeekNote data={data} /> : undefined}
      bodyClassName="px-3 pb-3"
    >
      {isLoading ? (
        <Bone className="mx-2 h-52 rounded-lg" />
      ) : !hasData ? (
        <div className="grid h-52 place-items-center px-6 text-center text-sm text-gray-500 dark:text-gray-400">
          {data?.isLive === false
            ? 'No shifts were due that week.'
            : "Bars appear here once this week's first shifts are due."}
        </div>
      ) : (
        <ChartContainer config={CARD_CONFIG} className="aspect-auto h-52 w-full">
          <BarChart data={days} barGap={3} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={6} />
            <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={40} />
            <ChartTooltip
              cursor={{ fill: 'rgba(0,74,107,0.06)' }}
              content={<ChartTooltipContent />}
            />
            <Bar
              dataKey="present"
              fill="var(--color-present)"
              radius={[4, 4, 0, 0]}
              maxBarSize={18}
            />
            <Bar
              dataKey="absent"
              fill="var(--color-absent)"
              fillOpacity={0.75}
              radius={[4, 4, 0, 0]}
              maxBarSize={18}
            />
          </BarChart>
        </ChartContainer>
      )}
    </DashboardTile>
  )
}

export function AttendanceOverviewModal({
  open,
  data,
  onClose,
}: {
  open: boolean
  data: OrganizationDashboard | undefined
  onClose: () => void
}): React.ReactElement | null {
  const week = useMemo(() => data?.weekAttendance ?? [], [data?.weekAttendance])
  const hasBreakdown = week.some((day) => day.late !== undefined)
  // Memoized so a re-render (refetch, filter) doesn't replay the bar animation.
  const chartDays = useMemo(
    () =>
      week.map((day) => ({
        label: day.label,
        onTime: Math.max(0, day.present - (day.late ?? 0)),
        late: day.late ?? 0,
        absent: absentOf(day),
        onLeave: day.onLeave ?? 0,
      })),
    [week]
  )
  if (!open) return null

  return (
    <DashboardDetailModal
      open
      onClose={onClose}
      title="Attendance overview"
      subtitle={
        data
          ? `${formatWeekRange(week.map((d) => d.date))}. Attended counts everyone who turned up; late is the part of it past shift start plus grace.`
          : undefined
      }
    >
      {!data ? (
        <Bone className="h-80 w-full rounded-lg" />
      ) : (
        <div className="space-y-6">
          <ChartContainer config={DETAIL_CONFIG} className="aspect-auto h-72 w-full">
            <BarChart data={chartDays} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={6} />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={44} />
              <ChartTooltip
                cursor={{ fill: 'rgba(0,74,107,0.06)' }}
                content={<ChartTooltipContent />}
              />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                formatter={(value: string) => (
                  <span className="text-gray-600 dark:text-gray-300">{value}</span>
                )}
              />
              <Bar
                dataKey="onTime"
                name="On time"
                stackId="attended"
                fill="var(--color-onTime)"
                maxBarSize={40}
              />
              {hasBreakdown ? (
                <Bar
                  dataKey="late"
                  name="Late"
                  stackId="attended"
                  fill="var(--color-late)"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={40}
                />
              ) : null}
              <Bar
                dataKey="absent"
                name="Absent"
                fill="var(--color-absent)"
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              />
              {hasBreakdown ? (
                <Bar
                  dataKey="onLeave"
                  name="On leave"
                  fill="var(--color-onLeave)"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={40}
                />
              ) : null}
            </BarChart>
          </ChartContainer>

          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left">
                    Day
                  </th>
                  <th scope="col" className="px-4 py-2 text-right">
                    Expected
                  </th>
                  <th scope="col" className="px-4 py-2 text-right">
                    Attended
                  </th>
                  {hasBreakdown ? (
                    <th scope="col" className="px-4 py-2 text-right">
                      Late
                    </th>
                  ) : null}
                  <th scope="col" className="px-4 py-2 text-right">
                    Absent
                  </th>
                  {hasBreakdown ? (
                    <th scope="col" className="px-4 py-2 text-right">
                      On leave
                    </th>
                  ) : null}
                  <th scope="col" className="px-4 py-2 text-right">
                    Rate
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 tabular-nums dark:divide-gray-700/70">
                {week.map((day) => (
                  <tr
                    key={day.date}
                    className={cn(day.presentPct === null && 'text-gray-400 dark:text-gray-500')}
                  >
                    <td className="whitespace-nowrap px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                      {day.label}{' '}
                      <span className="font-normal text-gray-400 dark:text-gray-500">
                        {day.date.slice(5)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">{formatNumber(day.recorded)}</td>
                    <td className="px-4 py-2.5 text-right">{formatNumber(day.present)}</td>
                    {hasBreakdown ? (
                      <td className="px-4 py-2.5 text-right">{formatNumber(day.late)}</td>
                    ) : null}
                    <td className="px-4 py-2.5 text-right">{formatNumber(absentOf(day))}</td>
                    {hasBreakdown ? (
                      <td className="px-4 py-2.5 text-right">{formatNumber(day.onLeave)}</td>
                    ) : null}
                    <td
                      className={cn(
                        'px-4 py-2.5 text-right font-semibold',
                        attendanceTone(day.presentPct)
                      )}
                    >
                      {formatPct(day.presentPct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <WeekNote data={data} />
        </div>
      )}
    </DashboardDetailModal>
  )
}
